import { AlertCircle, FileText, X } from 'lucide-react';
import { Button } from '../primitives/button';
import { Spinner } from '../primitives/spinner';
import { cn } from '../lib/cn';

export interface FileGalleryItem {
  id: string;
  name: string;
  /** Photos show as tiles with their preview; documents as rows */
  kind: 'image' | 'document';
  status: 'uploading' | 'ready' | 'failed';
  /** Opens the file (a signed link); only when ready */
  url?: string | null;
  /** Small preview for photos; the photo itself is used while there is none */
  thumbnailUrl?: string | null;
  /** Already formatted, e.g. "2.4 MB" */
  size?: string;
  /** Shown under a failed file */
  error?: string;
}

interface FileGalleryProps {
  items: FileGalleryItem[];
  /** Shows a remove button on every file that is not uploading */
  onRemove?: (item: FileGalleryItem) => void;
  labels: { remove: (name: string) => string; uploading: string; failed: string; preparing: string };
  className?: string;
}

/** Uploaded photos as a grid of previews and documents as a list, each opening in a new tab. */
export function FileGallery({ items, onRemove, labels, className }: FileGalleryProps) {
  const images = items.filter((item) => item.kind === 'image');
  const documents = items.filter((item) => item.kind === 'document');
  const remove = (item: FileGalleryItem) =>
    onRemove &&
    item.status !== 'uploading' && (
      <Button
        variant="secondary"
        size="icon"
        className="size-7 shadow-sm"
        aria-label={labels.remove(item.name)}
        onClick={() => onRemove(item)}
      >
        <X />
      </Button>
    );

  return (
    <div className={cn('grid gap-3', className)}>
      {images.length > 0 && (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {images.map((item) => {
            const preview = item.thumbnailUrl ?? item.url;
            return (
              <li key={item.id} className="relative">
                <a
                  href={item.status === 'ready' && item.url ? item.url : undefined}
                  target="_blank"
                  rel="noreferrer"
                  className="flex aspect-[4/3] items-center justify-center overflow-hidden rounded-lg border border-border bg-muted focus-visible:outline-2 focus-visible:outline-ring"
                >
                  {item.status === 'uploading' ? (
                    <span className="grid justify-items-center gap-2 text-xs text-muted-foreground">
                      <Spinner />
                      {labels.uploading}
                    </span>
                  ) : item.status === 'failed' ? (
                    <span className="grid justify-items-center gap-1 px-2 text-center text-xs text-danger">
                      <AlertCircle className="size-5" aria-hidden />
                      {item.error ?? labels.failed}
                    </span>
                  ) : preview ? (
                    <img src={preview} alt={item.name} loading="lazy" className="size-full object-cover" />
                  ) : (
                    <span className="text-xs text-muted-foreground">{labels.preparing}</span>
                  )}
                </a>
                <span className="absolute right-1.5 top-1.5">{remove(item)}</span>
                <span className="sr-only">{item.name}</span>
              </li>
            );
          })}
        </ul>
      )}
      {documents.length > 0 && (
        <ul className="grid gap-2">
          {documents.map((item) => (
            <li
              key={item.id}
              className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2 text-sm"
            >
              {item.status === 'uploading' ? (
                <Spinner className="size-4 shrink-0" />
              ) : item.status === 'failed' ? (
                <AlertCircle className="size-4 shrink-0 text-danger" aria-hidden />
              ) : (
                <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              )}
              <span className="grid min-w-0 flex-1">
                {item.status === 'ready' && item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="truncate font-medium underline-offset-2 hover:underline"
                  >
                    {item.name}
                  </a>
                ) : (
                  <span className="truncate">{item.name}</span>
                )}
                {item.status === 'failed' && (
                  <span className="text-xs text-danger">{item.error ?? labels.failed}</span>
                )}
                {item.status === 'uploading' && (
                  <span className="text-xs text-muted-foreground">{labels.uploading}</span>
                )}
              </span>
              {item.size && <span className="text-xs text-muted-foreground tabular-nums">{item.size}</span>}
              {remove(item)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
